<template>
	<div class="conquest-map">
		<svg viewBox="0 0 975 610" preserveAspectRatio="xMidYMid meet" class="conquest-svg">
			<defs>
				<radialGradient id="cq-halo" cx="50%" cy="50%" r="50%">
					<stop offset="0%" stop-color="#6ea8ff" stop-opacity="0.9" />
					<stop offset="100%" stop-color="#335cff" stop-opacity="0" />
				</radialGradient>
				<radialGradient id="cq-halo-hub" cx="50%" cy="50%" r="50%">
					<stop offset="0%" stop-color="#ffe39a" stop-opacity="0.95" />
					<stop offset="100%" stop-color="#ffce4d" stop-opacity="0" />
				</radialGradient>
				<filter id="cq-glow" x="-60%" y="-60%" width="220%" height="220%">
					<feGaussianBlur stdDeviation="3" result="b" />
					<feMerge>
						<feMergeNode in="b" />
						<feMergeNode in="SourceGraphic" />
					</feMerge>
				</filter>
			</defs>

			<!-- Nation silhouette -->
			<path v-if="nationPath" :d="nationPath" class="cq-nation" />

			<!-- State borders -->
			<path v-for="(d, i) in statePaths" :key="'s' + i" :d="d" class="cq-state" />

			<!-- Spreading connection arcs -->
			<path
				v-for="(a, i) in arcs"
				:key="'l' + i"
				:d="a.d"
				class="cq-link"
				:style="{ '--d': a.delay + 's' }"
			/>

			<!-- Conquest pins -->
			<g v-for="(p, i) in pins" :key="'p' + i" :transform="`translate(${p.x},${p.y})`">
				<g class="cq-pin" :class="{ hub: p.hub }" :style="{ '--d': p.delay + 's' }">
					<circle class="cq-pin-halo" :r="p.hub ? 22 : 14" :fill="p.hub ? 'url(#cq-halo-hub)' : 'url(#cq-halo)'" />
					<circle class="cq-pin-core" :r="p.hub ? 5 : 3.4" filter="url(#cq-glow)" />
					<circle class="cq-pin-dot" :r="p.hub ? 2 : 1.4" />
				</g>
			</g>

			<!-- Brand favicons riding the middle of each arc -->
			<g v-for="(b, i) in badges" :key="'b' + i" :transform="`translate(${b.x},${b.y})`">
				<circle class="cq-badge-pulse" r="13" :style="{ '--d': b.delay + 's' }" />
				<g class="cq-badge" :style="{ '--d': b.delay + 's' }">
					<image
						:href="brandIcon(b.brand)"
						:x="-iconSize(b.brand) / 2"
						:y="-iconSize(b.brand) / 2"
						:width="iconSize(b.brand)"
						:height="iconSize(b.brand)"
					/>
				</g>
			</g>
		</svg>
	</div>
</template>

<script>
import * as topojson from 'topojson-client'
import { geoPath, geoAlbersUsa } from 'd3-geo'

// [name, longitude, latitude, isHub]
const CITIES = [
	['Honolulu', -157.86, 21.31, false],
	['Anchorage', -149.9, 61.22, false],
	['Seattle', -122.33, 47.61, false],
	['Spokane', -117.43, 47.66, false],
	['Portland', -122.68, 45.52, false],
	['Sacramento', -121.49, 38.58, false],
	['San Francisco', -122.42, 37.77, false],
	['Reno', -119.81, 39.53, false],
	['Los Angeles', -118.24, 34.05, false],
	['San Diego', -117.16, 32.72, false],
	['Las Vegas', -115.14, 36.17, false],
	['Boise', -116.2, 43.62, false],
	['Phoenix', -112.07, 33.45, false],
	['Salt Lake City', -111.89, 40.76, false],
	['El Paso', -106.49, 31.76, false],
	['Billings', -108.5, 45.78, false],
	['Albuquerque', -106.65, 35.08, false],
	['Denver', -104.99, 39.74, false],
	['Cheyenne', -104.82, 41.14, false],
	['San Antonio', -98.49, 29.42, false],
	['Dallas', -96.8, 32.78, false],
	['Austin', -97.74, 30.27, false],
	['Houston', -95.37, 29.76, false],
	['Oklahoma City', -97.52, 35.47, false],
	['Wichita', -97.34, 37.69, false],
	['Kansas City', -94.58, 39.1, false],
	['Omaha', -95.93, 41.26, false],
	['Sioux Falls', -96.73, 43.54, false],
	['Fargo', -96.79, 46.88, false],
	['Des Moines', -93.61, 41.59, false],
	['Minneapolis', -93.27, 44.98, false],
	['New Orleans', -90.07, 29.95, false],
	['Little Rock', -92.29, 34.75, false],
	['Memphis', -90.05, 35.15, false],
	['St. Louis', -90.2, 38.63, false],
	['Madison', -89.4, 43.07, true],
	['Green Bay', -88.01, 44.51, false],
	['Milwaukee', -87.91, 43.04, false],
	['Chicago', -87.63, 41.88, false],
	['Birmingham', -86.8, 33.52, false],
	['Nashville', -86.78, 36.16, false],
	['Indianapolis', -86.16, 39.77, false],
	['Louisville', -85.76, 38.25, false],
	['Atlanta', -84.39, 33.75, false],
	['Detroit', -83.05, 42.33, false],
	['Columbus', -82.99, 39.96, false],
	['Tampa', -82.46, 27.95, false],
	['Jacksonville', -81.66, 30.33, false],
	['Cleveland', -81.69, 41.5, false],
	['Orlando', -81.38, 28.54, false],
	['Columbia', -81.03, 34.0, false],
	['Charlotte', -80.84, 35.23, false],
	['Miami', -80.19, 25.76, false],
	['Pittsburgh', -79.996, 40.44, false],
	['Buffalo', -78.88, 42.89, false],
	['Raleigh', -78.64, 35.78, false],
	['Richmond', -77.44, 37.54, false],
	['Washington', -77.04, 38.91, false],
	['Philadelphia', -75.17, 39.95, false],
	['New York', -74.01, 40.71, false],
	['Hartford', -72.69, 41.76, false],
	['Boston', -71.06, 42.36, false],
	['Portland ME', -70.26, 43.66, false],
]

// arcs radiate from the Madison hub; each carries a brand mark at its midpoint
const ARCS = [
	['Seattle', 'townweb'],
	['San Francisco', 'heygov'],
	['Los Angeles', 'clerkminutes'],
	['Denver', 'townweb'],
	['Dallas', 'heygov'],
	['Houston', 'clerkminutes'],
	['Atlanta', 'townweb'],
	['Miami', 'heygov'],
	['New York', 'clerkminutes'],
	['Boston', 'townweb'],
]

export default {
	name: 'ConquestMap',
	data() {
		return {
			nationPath: '',
			statePaths: [],
			pins: [],
			arcs: [],
			badges: [],
		}
	},
	async mounted() {
		try {
			const res = await fetch('/data/us-states.json')
			const topology = await res.json()
			this.render(topology)
		} catch (e) {
			// Map data is decorative — fail silently so the form is never blocked
			console.warn('ConquestMap: could not load US map data', e)
		}
	},
	methods: {
		brandIcon(brand) {
			return {
				townweb: '/images/brands/townweb-icon.png',
				heygov: '/images/brands/heygov-icon.png',
				clerkminutes: '/images/brands/clerkminutes-icon.svg',
			}[brand]
		},
		iconSize(brand) {
			// per-brand tuning so every mark reads at the same visual weight
			if (brand === 'townweb') return 30
			if (brand === 'clerkminutes') return 18
			return 24
		},
		render(topology) {
			const projection = geoAlbersUsa().scale(1300).translate([487.5, 305])
			const path = geoPath().projection(projection)

			if (topology.objects.nation) {
				this.nationPath = path(topojson.feature(topology, topology.objects.nation))
			}
			const states = topojson.feature(topology, topology.objects.states)
			this.statePaths = states.features.map(f => path(f)).filter(Boolean)

			// project pins, sorted west→east so the reveal sweeps across the map
			const byName = {}
			let hub = null
			const sorted = [...CITIES].sort((a, b) => a[1] - b[1])
			this.pins = sorted
				.map(([name, lng, lat, isHub], i) => {
					const xy = projection([lng, lat])
					if (!xy) return null
					const pin = { name, x: xy[0], y: xy[1], hub: isHub, delay: 0.25 + i * 0.035 }
					byName[name] = pin
					if (isHub) hub = pin
					return pin
				})
				.filter(Boolean)

			if (!hub) return

			this.arcs = []
			this.badges = []
			ARCS.forEach(([target, brand], i) => {
				const b = byName[target]
				if (!b) return
				const dx = b.x - hub.x
				const lift = Math.abs(dx) * 0.16
				const cx = (hub.x + b.x) / 2
				const cy = (hub.y + b.y) / 2 - lift
				const delay = 0.6 + i * 0.16
				this.arcs.push({ d: `M${hub.x},${hub.y} Q${cx},${cy} ${b.x},${b.y}`, delay })
				this.badges.push({
					x: (hub.x + b.x) / 2,
					y: (hub.y + b.y) / 2 - lift / 2,
					brand,
					delay: delay + 0.7,
				})
			})
		},
	},
}
</script>

<style scoped>
.conquest-map {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}
.conquest-svg {
	width: 100%;
	height: auto;
	max-height: 100%;
	overflow: visible;
}

.cq-nation {
	fill: rgba(120, 170, 255, 0.05);
	stroke: rgba(150, 190, 255, 0.18);
	stroke-width: 1;
}
.cq-state {
	fill: transparent;
	stroke: rgba(150, 190, 255, 0.1);
	stroke-width: 0.6;
}

.cq-link {
	fill: none;
	stroke: rgba(110, 168, 255, 0.45);
	stroke-width: 1;
	stroke-linecap: round;
	stroke-dasharray: 6 8;
	opacity: 0;
	animation: cq-link-in 0.9s ease forwards var(--d), cq-link-flow 14s linear infinite var(--d);
}

.cq-pin {
	transform-box: fill-box;
	transform-origin: center;
	opacity: 0;
	animation: cq-appear 0.55s cubic-bezier(0.2, 0.9, 0.25, 1) forwards;
	animation-delay: var(--d);
}
.cq-pin-halo {
	transform-box: fill-box;
	transform-origin: center;
	opacity: 0;
	animation: cq-pulse 3s ease-out infinite;
	animation-delay: calc(var(--d) + 0.3s);
}
.cq-pin-core {
	fill: #8ec2ff;
}
.cq-pin.hub .cq-pin-core {
	fill: #ffd86b;
}
.cq-pin-dot {
	fill: #ffffff;
}

/* branded arc badges */
.cq-badge {
	transform-box: fill-box;
	transform-origin: center;
	opacity: 0;
	animation: cq-badge-in 0.5s cubic-bezier(0.2, 0.9, 0.25, 1) forwards;
	animation-delay: var(--d);
}
.cq-badge-pulse {
	fill: none;
	stroke: rgba(142, 194, 255, 0.5);
	stroke-width: 1.5;
	transform-box: fill-box;
	transform-origin: center;
	opacity: 0;
	animation: cq-badge-ring 3.4s ease-out infinite;
	animation-delay: calc(var(--d) + 0.2s);
}

@keyframes cq-appear {
	0% {
		opacity: 0;
		transform: scale(0);
	}
	60% {
		opacity: 1;
		transform: scale(1.3);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
}
@keyframes cq-pulse {
	0% {
		opacity: 0.6;
		transform: scale(0.35);
	}
	80% {
		opacity: 0;
		transform: scale(2.4);
	}
	100% {
		opacity: 0;
		transform: scale(2.4);
	}
}
@keyframes cq-badge-in {
	0% {
		opacity: 0;
		transform: scale(0.2);
	}
	70% {
		opacity: 1;
		transform: scale(1.18);
	}
	100% {
		opacity: 1;
		transform: scale(1);
	}
}
@keyframes cq-badge-ring {
	0% {
		opacity: 0.55;
		transform: scale(0.85);
	}
	75% {
		opacity: 0;
		transform: scale(1.9);
	}
	100% {
		opacity: 0;
		transform: scale(1.9);
	}
}
@keyframes cq-link-in {
	to {
		opacity: 1;
	}
}
@keyframes cq-link-flow {
	to {
		stroke-dashoffset: -280;
	}
}

@media (prefers-reduced-motion: reduce) {
	.cq-pin,
	.cq-pin-halo,
	.cq-link,
	.cq-badge,
	.cq-badge-pulse {
		animation: none;
		opacity: 1;
	}
}
</style>
